import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setCampaignInfo } from '../slices/campaignSlice'
import { SHARED_TAGS } from '../slices/constants'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const campaignsApi = createApi({
  reducerPath: 'campaignsApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'CampaignDetails',
    'CampaignExcludedGroups',
    'CampaignList',
    SHARED_TAGS.CAMPAIGN_CATEGORY_INFO,
    'Translations'
  ],
  endpoints: (builder) => ({
    getCampaigns: builder.query({
      query: (requestParams: GetCampaignsParam) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'searchCampaigns',
          requestParams
        ),
      providesTags: [{ type: 'CampaignList' }]
    }),
    getAllCategoryList: builder.query({
      query: (requestParams: GetCategoryListParam) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getAllCategoryList',
          requestParams
        )
    }),
    getCampaignDetails: builder.query({
      query: (requestParams: GetCampaignDetailsParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getCampaignDetailsByID',
          requestParams
        ),
      providesTags: [{ type: 'CampaignDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetCampaignDetailsResponse
          }
          if (data && data.result) {
            dispatch(setCampaignInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    changeCampaignStatus: builder.mutation({
      query: (requestParams: ChangeCampaignStatusParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'changeCampaignStatus',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignDetails' }, { type: 'CampaignList' }]
    }),
    editCampaignNotes: builder.mutation({
      query: (requestParams: EditCampaignNotesParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'editCampaignNotes',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignDetails' }]
    }),
    validateCampaignNotes: builder.mutation({
      query: (requestParams: ValidateCampaignNotesParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'validationForCampaignNotes',
          requestParams
        )
    }),
    getExcludedCampaignGroup: builder.query({
      query: (requestParams: { CampaignID: number }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getExcludedCampaignGroup',
          requestParams
        ),
      providesTags: [{ type: 'CampaignExcludedGroups' }]
    }),
    addCampaignToGroup: builder.mutation({
      query: (requestParams: CampaignTargetGroupsParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'addCampaignToGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'CampaignDetails' },
        { type: 'CampaignExcludedGroups' }
      ]
    }),
    excludeCampaignFromGroup: builder.mutation({
      query: (requestParams: CampaignTargetGroupsParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'excludeCampaignFromGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'CampaignDetails' },
        { type: 'CampaignExcludedGroups' }
      ]
    }),
    removeCampaignFromGroup: builder.mutation({
      query: (requestParams: RemoveCampaignTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'removeCampaignFromGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'CampaignDetails' },
        { type: 'CampaignExcludedGroups' }
      ]
    }),
    getAllInfoforCampaigns: builder.query({
      query: (requestParams: GetAllForCampaignsParam) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'AllforCampaigns',
          requestParams
        )
    }),
    getFeaturedCampaigns: builder.query({
      query: (requestParams: GetCategoryListParam) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getFeaturedCampaigns',
          requestParams
        )
    }),
    getCampaignsFromCategory: builder.query({
      query: (requestParams: GetCampaignsInfoWithCategoryParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getCampaignInfoWithCategory',
          requestParams
        )
    }),
    validateCampaignSteps: builder.mutation({
      query: (requestParams: CampaignValidationParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'validateCampaignSteps',
          requestParams
        )
    }),
    createCampaign: builder.mutation({
      query: (requestParams: BaseCampaign) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'createCampaign',
          requestParams
        )
    }),
    updateCampaign: builder.mutation({
      query: (requestParams: BaseCampaign) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'updateCampaign',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignDetails' }]
    }),
    fetchCampaignsForPeriod: builder.mutation({
      query: (requestParams: FetchDataWithPeriodParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'fetchCampaignsForPeriod',
          requestParams
        )
    }),
    duplicateCampaign: builder.mutation({
      query: (requestParams: BaseCampaign) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'duplicateCampaign',
          requestParams
        )
    }),
    updateCampaignUiPositions: builder.mutation({
      query: (requestParams: UpdateCampaignUisParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'updateCampaignUiPositions',
          requestParams
        ),
      invalidatesTags: [SHARED_TAGS.CAMPAIGN_CATEGORY_INFO]
    }),
    softDeleteCampaigns: builder.mutation({
      query: (requestParams: { CampaignID: number }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'softDeleteCampaigns',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignList' }]
    }),
    getAllActiveCampaignsForReferral: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getAllActiveCampaignsForReferral',
          requestParams
        )
    }),
    getCampaignUserParticipations: builder.query({
      query: (requestParams: GetCampaignParticipationsParam) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getCampaignUserParticipations',
          requestParams
        )
    }),
    downloadParticipationsFromCampaign: builder.mutation({
      query: (requestParams: DownloadFromCampaignParticipationsParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'downloadParticipationsFromCampaign',
          requestParams
        )
    }),
    validateCampaignEarningConditions: builder.mutation({
      query: (requestParams: { objectToValidate: EarningCondition }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'validateCampaignEarningCondition',
          requestParams
        )
    }),
    getAllActiveCampaignsForTenant: builder.query<
      ApiResponse<GetAllActiveCampaignsForTenantResponse[]>,
      GetAllActiveCampaignsForTenantParams
    >({
      query: (requestParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getAllActiveCampaignsForTenant',
          requestParams
        )
    })
  })
})

export const {
  useChangeCampaignStatusMutation,
  useEditCampaignNotesMutation,
  useValidateCampaignNotesMutation,
  useGetAllCategoryListQuery,
  useGetCampaignDetailsQuery,
  useGetCampaignsQuery,
  useGetExcludedCampaignGroupQuery,
  useAddCampaignToGroupMutation,
  useExcludeCampaignFromGroupMutation,
  useRemoveCampaignFromGroupMutation,
  useGetAllInfoforCampaignsQuery,
  useLazyGetCampaignsFromCategoryQuery,
  useGetCampaignsFromCategoryQuery,
  useValidateCampaignStepsMutation,
  useGetFeaturedCampaignsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useFetchCampaignsForPeriodMutation,
  useDuplicateCampaignMutation,
  useUpdateCampaignUiPositionsMutation,
  useSoftDeleteCampaignsMutation,
  useGetAllActiveCampaignsForReferralQuery,
  useGetCampaignUserParticipationsQuery,
  useDownloadParticipationsFromCampaignMutation,
  useValidateCampaignEarningConditionsMutation,
  useGetAllActiveCampaignsForTenantQuery
} = campaignsApi
